import moment from 'moment-timezone';
import PerformanceUtils from 'services/PerformanceUtils';
import _ from 'lodash';
import { Task } from 'generated/api';

class TaskUtils {
  filterTodaysTasks(tasks: Task[] = []): Task[] {
    const midnight = moment().add(1, 'day').startOf('day');
    return tasks.filter((task) => {
      // Check if the task is not archived and has a status of false
      if (!task.archived && !task.status) {
        // Check if the task's date is before the next day's midnight
        if (moment.unix(task.date!).isBefore(midnight)) {
          return true;
        }
        // If there's a due_date, check that as well
        if (task.due_date && moment.unix(task.due_date).isBefore(midnight)) {
          return true;
        }
      }
      return false;
    });
  }

  filterStatus(tasks: Task[], status: boolean): Task[] {
    return tasks.filter((task) => task.status === status);
  }

  filterTasks(tasks: Task[] = [], archived: boolean = false): Task[] {
    return tasks.filter((task) => {
      return archived ? task.archived === archived : !task.archived;
    });
  }

  isRelevant(task: Task): boolean {
    return !task.status && !!task.date && moment.unix(task.date).startOf('day').isBefore(moment());
  }

  isDue(task: Task): boolean {
    return (
      !task.status &&
      !!task.due_date &&
      moment.unix(task.due_date).startOf('day').isBefore(moment())
    );
  }

  countUnreadComments(tasks: Task[] = [], ownId: string): number {
    return tasks.reduce((acc, task) => {
      const unread = this.getUnreadComments(task, ownId);
      return unread > 0 ? acc + unread : acc;
    }, 0);
  }

  totalUnreadBadge(tasks: Task[] = [], ownId: string): number {
    tasks = _.uniqBy(tasks, 'id');
    let unarchivedTasks = this.filterTasks(tasks);
    let unfinishedTasks = this.filterStatus(unarchivedTasks, false);
    let tasksCount = unfinishedTasks.map((task) => {
      let numberComments = this.getUnreadComments(task, ownId);
      let isUnreadTask = !(task.read_by || []).includes(ownId);
      return {
        task,
        count: numberComments || isUnreadTask ? 1 : 0,
      };
    });

    return tasksCount.map((data) => data.count).reduce((acc, val) => acc + val, 0);
  }

  getUnreadComments(task: Task, id: string, commentsKey: string = 'comments'): number {
    return ((task as any)[commentsKey] || []).reduce((count: number, comment: any) => {
      if (!comment.notifications_disabled && !comment.read_by?.includes(id)) {
        count++;
      }
      return count;
    }, 0);
  }

  // Mirrors the UnreadBadge logic in TaskBadge/view.jsx (unreadComments > 0 || isUnread)
  // so the list-sort signal and the red dot can't drift apart.
  hasUnreadSignal(task: Task, ownId: string): boolean {
    if (!ownId) return false;
    if (this.getUnreadComments(task, ownId) > 0) return true;
    return !(task.read_by || []).includes(ownId);
  }

  sortTasks(tasks: Task[] = []): Task[] {
    let finished = this.filterStatus(tasks, true);
    let unfinished = this.filterStatus(tasks, false);

    finished = PerformanceUtils.sortBy(finished, ['date']);

    return [...unfinished, ...finished];
  }
}

export default new TaskUtils();
